import { AlertCircle, Check, Loader2, Save } from 'lucide-react'
import type { ReactNode } from 'react'
import type { AutoSaveState } from '@/hooks/use-auto-save'
import { assertNever } from '@/lib/assert-never'
import { cn } from '@/lib/utils'

/** Border colour used by every auto-saving control to signal its save state. */
export function autoSaveBorderClass(state: AutoSaveState): string {
  switch (state) {
    case 'pending':
      return 'border-muted-foreground/50'
    case 'saving':
      return 'border-blue-500'
    case 'saved':
      return 'border-green-500'
    case 'error':
      return 'border-red-500'
    case 'idle':
      return ''
    default:
      return assertNever(state, 'Unexpected auto-save state')
  }
}

interface AutoSaveStatusIconProps {
  state: AutoSaveState
  /** Sizing classes for the icon (controls differ: h-3.5 inside inputs, h-4 on buttons). */
  className?: string
  /** Rendered while idle — a chevron for dropdowns, nothing for inputs. */
  idle?: ReactNode
}

/** The save-status glyph: unsaved changes, spinner, tick, or alert. */
export function AutoSaveStatusIcon({
  state,
  className,
  idle = null,
}: AutoSaveStatusIconProps) {
  switch (state) {
    case 'pending':
      return <Save className={cn('text-muted-foreground', className)} />
    case 'saving':
      return <Loader2 className={cn('animate-spin text-blue-600', className)} />
    case 'saved':
      return <Check className={cn('text-green-600', className)} />
    case 'error':
      return <AlertCircle className={cn('text-red-600', className)} />
    case 'idle':
      return idle
    default:
      return assertNever(state, 'Unexpected auto-save state')
  }
}
